import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  PageHeader,
  TextAreaField,
  TextField,
} from '@safepos/ui';
import { FEATURES, type FeatureKey, formatRupiah } from '@safepos/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { LicenseInfo } from '../lib/license';
import { useSubmit } from '../lib/useForm';
import { OnboardingCard } from '../components/OnboardingCard';
import { Bars } from './ReportPages';

export function DashboardPage() {
  const { me, can } = useAuth();
  const summary = useQuery({
    queryKey: ['catalog-summary'],
    enabled: can('catalog:read'),
    queryFn: () =>
      api.get<{ products: number; variants: number; categories: number; modifierGroups: number }>(
        '/catalog/summary',
      ),
  });
  const s = summary.data;
  const dash = useQuery({
    queryKey: ['dashboard'],
    enabled: can('reports:read'),
    refetchInterval: 30_000,
    queryFn: () =>
      api.get<{
        today: {
          date: string;
          net: number;
          orders: number;
          avgBasket: number;
          voids: number;
          refunds: number;
        };
        week: { net: number; orders: number };
        trend: { date: string; net: number }[];
        hours: { hour: string; net: number; orders: number }[];
        topProducts: { name: string; qty: number; net: number }[];
        alerts: {
          negativeStock: number;
          lowStock: number;
          flaggedOrders: number;
          openShifts: number;
        };
      }>('/reports/dashboard'),
  });
  const d = dash.data;
  return (
    <>
      <PageHeader
        title={`Halo, ${me?.user.name ?? ''}`}
        subtitle={`${me?.tenant.name} · ${me?.role.name}`}
      />
      <OnboardingCard />
      {d && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {(
              [
                ['Penjualan hari ini', formatRupiah(d.today.net)],
                ['Transaksi', d.today.orders.toLocaleString('id-ID')],
                ['Rata-rata/transaksi', formatRupiah(d.today.avgBasket)],
                ['7 hari terakhir', formatRupiah(d.week.net)],
              ] as const
            ).map(([k, v]) => (
              <Card key={k}>
                <div className="text-sm text-slate-600">{k}</div>
                <div className="text-2xl font-semibold">{v}</div>
              </Card>
            ))}
          </div>
          {(d.alerts.negativeStock > 0 ||
            d.alerts.lowStock > 0 ||
            d.alerts.flaggedOrders > 0 ||
            d.today.voids > 0) && (
            <div className="mb-4 space-y-2">
              {d.alerts.negativeStock > 0 && (
                <Alert>
                  {d.alerts.negativeStock} barang stok negatif —{' '}
                  <Link className="underline" to="/office/stok">
                    lihat stok
                  </Link>
                </Alert>
              )}
              {d.alerts.lowStock > 0 && (
                <Alert tone="info">
                  {d.alerts.lowStock} barang di bawah stok minimum —{' '}
                  <Link className="underline" to="/office/stok">
                    lihat stok
                  </Link>
                </Alert>
              )}
              {d.alerts.flaggedOrders > 0 && (
                <Alert tone="info">
                  {d.alerts.flaggedOrders} transaksi offline perlu ditinjau —{' '}
                  <Link className="underline" to="/office/transaksi">
                    buka transaksi
                  </Link>
                </Alert>
              )}
              {d.today.voids > 0 && (
                <Alert tone="info">{d.today.voids} transaksi dibatalkan hari ini.</Alert>
              )}
            </div>
          )}
          <div className="mb-4 grid gap-4 lg:grid-cols-2">
            <Card
              title="Penjualan 7 hari"
              actions={
                <Link className="text-sm text-brand-700" to="/office/laporan">
                  Laporan lengkap →
                </Link>
              }
            >
              <Bars data={d.trend.map((x) => ({ label: x.date.slice(5), value: x.net }))} />
            </Card>
            <Card title="Penjualan hari ini per jam">
              <Bars data={d.hours.map((x) => ({ label: x.hour, value: x.net }))} />
            </Card>
          </div>
          <Card title="Produk terlaris hari ini">
            {d.topProducts.length === 0 ? (
              <p className="text-sm text-slate-500">Belum ada penjualan hari ini.</p>
            ) : (
              <ol className="space-y-1 text-sm">
                {d.topProducts.map((p) => (
                  <li key={p.name} className="flex justify-between">
                    <span>
                      {p.name} <span className="text-slate-500">× {p.qty}</span>
                    </span>
                    <span>{formatRupiah(p.net)}</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>
          <div className="mb-4" />
        </>
      )}
      {s && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {(
            [
              ['Produk', s.products],
              ['Varian', s.variants],
              ['Kategori', s.categories],
              ['Grup modifier', s.modifierGroups],
            ] as const
          ).map(([k, v]) => (
            <Card key={k}>
              <div className="text-sm text-slate-600">{k}</div>
              <div className="text-3xl font-semibold">{v.toLocaleString('id-ID')}</div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

interface Sub {
  status: string;
  trialEndsAt: string | null;
  plan: { name: string; priceMonthly: number } | null;
  limits: Record<string, number>;
  usage: Record<string, number>;
  license?: LicenseInfo;
}

const dateLong = (s: string) =>
  new Date(`${s}T00:00:00`).toLocaleDateString('id-ID', { dateStyle: 'long' });

/** Ringkasan lisensi (self-hosted) + pemasangan lisensi baru untuk perpanjangan/penambahan fitur (hanya Owner). */
function LicenseCard({ lic }: { lic: LicenseInfo }) {
  const { can } = useAuth();
  const [token, setToken] = useState('');
  const install = useSubmit(async () => {
    await api.put('/license', { token: token.trim() });
    // fitur & batas berubah: muat ulang agar menu dan akses ikut diperbarui
    window.location.reload();
  });
  return (
    <>
      <Card
        title={`Lisensi · ${lic.customer}`}
        actions={
          <Badge tone={lic.updatesActive ? 'success' : 'warning'}>
            {lic.updatesActive
              ? `Update aktif · ${lic.updatesDaysLeft} hari lagi`
              : 'Masa update berakhir'}
          </Badge>
        }
      >
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-600">ID lisensi</dt>
            <dd className="font-mono">{lic.licenseId}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-600">Berhak update s.d.</dt>
            <dd>{dateLong(lic.updatesUntil)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-600">Versi aplikasi</dt>
            <dd>{lic.releaseDate ?? '—'}</dd>
          </div>
        </dl>
        {!lic.updatesActive && (
          <div className="mt-3">
            <Alert tone="info">
              Aplikasi tetap berfungsi penuh. Hubungi penjual untuk memperpanjang masa update agar
              dapat memasang versi baru.
            </Alert>
          </div>
        )}
        <div className="mt-4">
          <div className="mb-1 text-sm font-medium">Fitur aktif</div>
          <div className="flex flex-wrap gap-1.5">
            {lic.features.length === 0 && (
              <span className="text-sm text-slate-500">Hanya fitur dasar</span>
            )}
            {lic.features.map((k) => (
              <Badge key={k} tone="info">
                {FEATURES[k as FeatureKey]?.replace(/\s*\(.*\)/, '') ?? k}
              </Badge>
            ))}
          </div>
        </div>
      </Card>
      {can('license:write') && (
        <div className="mt-4">
          <Card title="Pasang lisensi baru">
            <form
              className="max-w-2xl space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void install.run();
              }}
            >
              <p className="text-sm text-slate-600">
                Untuk perpanjangan masa update atau penambahan fitur: tempel isi berkas lisensi baru
                dari penjual (diawali <code>SPL1.</code>). Lisensi yang lebih lama dari yang
                terpasang ditolak.
              </p>
              {install.error && <Alert>{install.error}</Alert>}
              <TextAreaField
                label="Isi lisensi"
                rows={4}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="font-mono text-xs"
                required
              />
              <Button type="submit" disabled={install.busy || token.trim().length < 20}>
                {install.busy ? 'Memasang…' : 'Pasang lisensi'}
              </Button>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}

export function SubscriptionPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<Sub>('/subscription'),
  });
  const label: Record<string, string> = {
    outlets: 'Outlet',
    devices: 'Perangkat',
    employees: 'Pegawai',
  };
  if (isLoading || !data) return <p>Memuat…</p>;
  const lic = data.license;
  return (
    <>
      <PageHeader
        title={lic ? 'Lisensi' : 'Langganan'}
        subtitle={lic ? 'Lisensi, fitur aktif, dan pemakaian' : 'Paket dan pemakaian'}
      />
      {lic && <LicenseCard lic={lic} />}
      <div className={lic ? 'mt-4' : ''}>
        <Card
          title={lic ? 'Pemakaian' : `Paket ${data.plan?.name ?? '—'}`}
          actions={
            lic ? undefined : (
              <Badge tone={data.status === 'active' ? 'success' : 'info'}>
                {data.status === 'trial' ? 'Masa uji coba' : data.status}
              </Badge>
            )
          }
        >
          {!lic && (
            <p className="mb-4 text-sm text-slate-600">
              {data.plan ? `${formatRupiah(data.plan.priceMonthly)} / bulan` : ''}
              {data.trialEndsAt &&
                ` · uji coba sampai ${new Date(data.trialEndsAt).toLocaleDateString('id-ID', { dateStyle: 'long' })}`}
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-3">
            {Object.keys(label).map((k) => {
              const used = data.usage[k] ?? 0;
              const max = data.limits[k] ?? 0;
              return (
                <div key={k} className="rounded-lg border border-slate-200 p-3">
                  <div className="text-sm text-slate-600">{label[k]}</div>
                  <div className="text-2xl font-semibold">
                    {used} <span className="text-base font-normal text-slate-500">/ {max}</span>
                  </div>
                  <div className="mt-2 h-2 rounded bg-slate-100">
                    <div
                      className="h-2 rounded bg-brand-500"
                      style={{ width: `${Math.min(100, max ? (used / max) * 100 : 0)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </>
  );
}

interface AuditRow {
  id: string;
  createdAt: string;
  actorType: string;
  action: string;
  entity: string;
  entityId: string | null;
  ip: string | null;
}

export function AuditPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit'],
    queryFn: () => api.get<{ data: AuditRow[] }>('/audit-logs?limit=100'),
  });
  const cols: Column<AuditRow>[] = [
    {
      header: 'Waktu',
      cell: (r) =>
        new Date(r.createdAt).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'medium' }),
    },
    { header: 'Pelaku', cell: (r) => <Badge>{r.actorType}</Badge> },
    { header: 'Aksi', cell: (r) => <code className="text-xs">{r.action}</code> },
    {
      header: 'Objek',
      cell: (r) => `${r.entity}${r.entityId ? ` · ${r.entityId.slice(0, 8)}` : ''}`,
    },
    { header: 'IP', cell: (r) => r.ip ?? '—' },
  ];
  return (
    <>
      <PageHeader title="Log Aktivitas" subtitle="Catatan aksi penting (tidak dapat diubah)" />
      {error && <Alert>{(error as Error).message}</Alert>}
      <Card>{isLoading ? 'Memuat…' : <DataTable columns={cols} rows={data?.data ?? []} />}</Card>
    </>
  );
}

export function SecurityPage() {
  const { me } = useAuth();
  const totpAllowed = me?.features?.totp !== false || (me?.user.totpEnabled ?? false);
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [enabled, setEnabled] = useState(me?.user.totpEnabled ?? false);
  const begin = useSubmit(async () => setSetup(await api.post('/auth/totp/setup')));
  const enable = useSubmit(async () => {
    await api.post('/auth/totp/enable', { code });
    setEnabled(true);
    setSetup(null);
    setCode('');
  });
  const disable = useSubmit(async () => {
    await api.post('/auth/totp/disable', { code });
    setEnabled(false);
    setCode('');
  });
  return (
    <>
      <PageHeader
        title="Keamanan Akun"
        subtitle="Verifikasi dua langkah (TOTP) dengan aplikasi autentikator"
      />
      {!totpAllowed && (
        <Alert tone="info">
          Verifikasi dua langkah tidak termasuk dalam paket Anda. Hubungi administrator untuk
          mengaktifkannya.
        </Alert>
      )}
      {totpAllowed && (
        <Card
          title="Autentikasi dua langkah"
          actions={
            <Badge tone={enabled ? 'success' : 'warning'}>{enabled ? 'Aktif' : 'Nonaktif'}</Badge>
          }
        >
          {(begin.error || enable.error || disable.error) && (
            <Alert>{begin.error ?? enable.error ?? disable.error}</Alert>
          )}
          {!enabled && !setup && (
            <Button onClick={() => void begin.run()} disabled={begin.busy}>
              Aktifkan
            </Button>
          )}
          {setup && (
            <form
              className="max-w-sm space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void enable.run();
              }}
            >
              <p className="text-sm text-slate-600">
                Tambahkan akun di aplikasi autentikator (Google Authenticator, Authy, dll.) dengan
                kunci rahasia berikut, lalu masukkan kode 6 digit.
              </p>
              <p className="select-all break-all rounded-lg bg-slate-100 p-3 font-mono text-sm">
                {setup.secret}
              </p>
              <TextField
                label="Kode 6 digit"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <Button type="submit" disabled={enable.busy}>
                Verifikasi & aktifkan
              </Button>
            </form>
          )}
          {enabled && (
            <form
              className="max-w-sm space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void disable.run();
              }}
            >
              <TextField
                label="Kode saat ini untuk menonaktifkan"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <Button type="submit" variant="danger" disabled={disable.busy}>
                Nonaktifkan
              </Button>
            </form>
          )}
        </Card>
      )}
    </>
  );
}
